import React, { useState } from 'react';
import { Box, Button, Card, CardContent, CardMedia, Container, CssBaseline, Grid, Tooltip, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import Footer from '../Footer/Footer';
import MultiLinePlot from '../MultiLinePlot/MultiLinePlot';
import SpeciesDropdown from '../SpeciesDropdown/SpeciesDropdown';
import StackedBarChart from '../StackedBarCharts/StackedBarCharts';
import posterior_flux_ch4_spatial_map from '../../data/posterior_flux_ch4_spatial_map.png';
import posterior_flux_n2o_spatial_map from '../../data/posterior_flux_n2o_spatial_map.png';

const chartCardSx = { height: '100%', backgroundColor: '#FFFFFF' };

const Summary = () => {
  const [selectedSpecies, setSelectedSpecies] = useState('CH4');
  const [selectedResolution, setSelectedResolution] = useState('monthly');

  const handleSpeciesChange = ({ species, resolution }) => {
    setSelectedSpecies(species);
    setSelectedResolution(resolution);
  };

  const saveAsPDF = () => {
    const summaryElement = document.getElementById('summary-content');
    if (!summaryElement) return;

    document.querySelectorAll('.no-print').forEach((element) => {
      (element as HTMLElement).style.display = 'none';
    });

    html2canvas(summaryElement, { scale: 2.5 }).then((canvas) => {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const margin = 8;
      const imgWidth = 210 - 2 * margin;
      const pageHeight = 297 - 2 * margin;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = margin;

      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', margin, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight + margin;
        pdf.addPage();
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', margin, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      document.querySelectorAll('.no-print').forEach((element) => {
        (element as HTMLElement).style.display = '';
      });
      pdf.save('summary.pdf');
    });
  };

  const mapImage = selectedSpecies === 'CH4' ? posterior_flux_ch4_spatial_map : posterior_flux_n2o_spatial_map;

  return (
    <Box sx={{ backgroundColor: 'background.default', minHeight: '100vh', pt: { xs: 8, md: 11 } }}>
      <CssBaseline />
      <Container maxWidth="xl" id="summary-content">
        <Box component="header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'flex-end' }, gap: 3, flexDirection: { xs: 'column', md: 'row' }, mb: 3 }}>
          <Box>
            <Typography variant="overline" color="primary" sx={{ fontWeight: 700, letterSpacing: 1.1 }}>GEMMA dashboard</Typography>
            <Typography variant="h3" component="h1" sx={{ mt: 0.5 }}>UK National Emissions</Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>Compare inventory and atmospheric data-derived emissions estimates.</Typography>
          </Box>
          <Button className="no-print" variant="outlined" onClick={saveAsPDF}>Save as PDF</Button>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', p: { xs: 2, md: 2.5 }, mb: 3, backgroundColor: '#FFFFFF', border: 1, borderColor: 'divider', borderLeft: '4px solid', borderLeftColor: 'primary.main' }}>
          <Box>
            <Typography variant="h6">Explore the data</Typography>
            <Typography variant="body2" color="text.secondary">Choose a gas and time resolution to update every view.</Typography>
          </Box>
          <SpeciesDropdown selectedSpecies={selectedSpecies} handleSpeciesChange={handleSpeciesChange} />
        </Box>

        <Typography color="error.main" sx={{ mb: 2, fontWeight: 600 }}>
          Provisional content subject to change.
        </Typography>

        <Card sx={{ ...chartCardSx, mb: 3 }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Box>
                <Typography variant="h5">Emissions over time</Typography>
                <Typography variant="body2" color="text.secondary">Model and inventory estimates for the selected species.</Typography>
              </Box>
              <Tooltip title="CO2 equivalence uses IPCC AR5 global warming potentials. Use the selector above to change the species and resolution.">
                <InfoIcon color="action" />
              </Tooltip>
            </Box>
            <MultiLinePlot selectedSpecies={selectedSpecies} selectedResolution={selectedResolution} />
          </CardContent>
        </Card>

        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} md={6}>
            <Card sx={chartCardSx}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box>
                    <Typography variant="h5">Spatial emissions</Typography>
                    <Typography variant="body2" color="text.secondary">Inversion estimates for 2018 to 2023.</Typography>
                  </Box>
                  <Tooltip title="Spatial distributions are inferred from atmospheric observations and influenced by initial inventory estimates.">
                    <InfoIcon color="action" />
                  </Tooltip>
                </Box>
                <CardMedia component="img" image={mapImage} alt={`${selectedSpecies} posterior flux spatial map`} sx={{ width: '100%', mt: 2 }} />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={chartCardSx}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box>
                    <Typography variant="h5">Emissions by sector</Typography>
                    <Typography variant="body2" color="text.secondary">Compare sector contributions across models.</Typography>
                  </Box>
                  <Tooltip title="Sector values show CO2 equivalent emissions from inventory and atmospheric data-derived estimates.">
                    <InfoIcon color="action" />
                  </Tooltip>
                </Box>
                <StackedBarChart selectedSpecies={selectedSpecies} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box className="no-print" sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', py: 5 }}>
          <Button className="interestedforupdates" variant="contained" href="https://forms.office.com/Pages/ResponsePage.aspx?id=MH_ksn3NTkql2rGM8aQVG4DmomKTI3lFj-w0q4uQS3RUMEU0VUFTQlhEVTEwTDRVOVJPVjcxQjFSSC4u" target="_blank" rel="noopener noreferrer">
            Register for updates
          </Button>
          <Button className="registerfeedback" variant="outlined" href="https://forms.office.com/Pages/ResponsePage.aspx?id=MH_ksn3NTkql2rGM8aQVG4DmomKTI3lFj-w0q4uQS3RUMzVFRVNaNVVHWjhWRVpDQzNMOUUwUk04NS4u" target="_blank" rel="noopener noreferrer">
            Register your feedback
          </Button>
        </Box>
        <Footer />
      </Container>
    </Box>
  );
};

export default Summary;
